import React from "react";
import { useNavigate } from "react-router-dom";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { IEvent, IProgram } from "../../../interfaces/models/IEvent";
import PresentationIcon from "../../../assets/images&icons/presentation.jpg";
import PreviewComponente from "../../../components/SlateEditor/Preview";
import CircularPhoto from "../../../components/Atomos/CircularPhoto/CircularPhoto";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { parseProgramDescription } from "../../../utils/parseProgramDescription";

interface EventHasEndedProps {
  event: IEvent;
  groupedPrograms: Record<string, IProgram[]>;
  eventTimeZone?: string;
}

const extractVideoId = (url: string): string | null => {
  if (!url) {
    return null;
  }
  
  const cleanUrl = url.trim();
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1] && match[1].length === 11) {
      return match[1];
    }
  }
  
  return null;
};

const buildYouTubeEmbedUrl = (videoId: string): string => {
  const params = new URLSearchParams({
    enablejsapi: '1',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

function EventHasEnded({ event, groupedPrograms, eventTimeZone = "UTC" }: EventHasEndedProps): React.JSX.Element {
  const navigate = useNavigate();

  if (!event) {
    return <div>Event not found</div>;
  }
  
  const postEventSessions = Object.values(groupedPrograms)
    .flat()
    .filter((program) => program.isPostEvent && (
      program.postEventDescription ||
      program.description ||
      program.youtubeLink ||
      (program.takeaways && program.takeaways.length > 0 && program.takeaways.some(t => t.trim() !== "")) ||
      (program.postEventSpeakers && program.postEventSpeakers.length > 0) ||
      (program.speakers && program.speakers.length > 0) ||
      (program.documents && program.documents.length > 0)
    ));

  if (postEventSessions.length === 0) {
    return (
      <div className="pb-4">
        <p className="text-left font-decimal font-[400] text-[26px] md:text-[30px] lg:text-[32px] mb-8 text-primary">Program</p>
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-gray-font text-lg font-Roboto">
            No post event session information available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 w-full">
      <p className="text-left font-decimal font-[400] text-[26px] md:text-[30px] lg:text-[32px] mb-8 text-primary">Program</p>
      <div className="flex flex-col gap-[10px]">
        {postEventSessions.map((program, index) => {
          const videoId = program.youtubeLink ? extractVideoId(program.youtubeLink) : null;
          const description = parseProgramDescription(program.postEventDescription || program.description);
          const takeaways = program.takeaways?.filter(t => t.trim() !== "") || [];
          const speakers = (program as any).postEventSpeakers || program.speakers || [];
          const hasPresentation =
            program.documents && program.documents.length > 0;
          const showMediaColumn = Boolean(videoId || program.video || hasPresentation);

          const presentationButtonIcon = (
            <div className="w-8 h-8 bg-[#CB3827] rounded-full flex items-center justify-center">
              <img src={PresentationIcon} alt="presentation" className="w-5 h-5 object-contain" />
            </div>
          );

          const presentationButtonClassName =
            "!p-0 bg-white bg-opacity-20 font-bold !text-[#004578] [&>div]:!justify-start [&>div]:gap-3 !text-[14px] lg:text-base justify-end";

          const handlePresentationClick = () => {
            if (!event.id) {
              return;
            }
            const programQuery = program.id ? `?programId=${program.id}` : "";
            navigate(`/events/${event.id}/program/pdf${programQuery}`);
          };

          return (
            <div key={`${program.title}-${index}`} className="flex flex-col gap-6 p-5 bg-[#F6F6F6] relative">
              <div className="absolute top-[10px] left-0 w-5 h-[0.8px] bg-primary"></div>
              
              <div className="flex flex-col gap-2">
                <h3 className="text-[#474F56] font-Roboto text-[20px] font-normal leading-6">
                  {program.title}
                </h3>
                {(program.location || program.venueName) && (
                  <p className="text-[#474F56] text-left font-Roboto font-normal text-lg">
                    {[program.location, program.venueName].filter(Boolean).join(" - ")}
                  </p>
                )}
              </div>

              {description && (
                <div className="text-gray-font text-left [&_*]:!bg-transparent">
                  <PreviewComponente
                    value={description}
                    showMessage={false}
                    className="!bg-transparent [&_p]:mb-2 [&_p]:text-gray-font [&_*]:!bg-transparent"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {takeaways.length > 0 && (
                  <div className="flex flex-col gap-2 lg:col-span-2 lg:mr-8">
                    <h4 className="text-primary font-decimal text-xl font-medium">
                      Takeaways
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-font font-Roboto">
                      {takeaways.map((takeaway, idx) => (
                        <li key={idx}>{takeaway}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {showMediaColumn && (
                <div className="w-full lg:col-span-1">
                  {videoId && (
                    <div className="w-full aspect-video bg-black rounded-lg flex items-center justify-center">
                      <iframe
                        className="w-full h-full rounded-lg"
                        src={buildYouTubeEmbedUrl(videoId)}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                  )}
                  {hasPresentation && !videoId && (
                    <div className="flex justify-end lg:justify-start h-full items-start">
                      <LegacyButton
                        onClick={handlePresentationClick}
                        text="VIEW PRESENTATION"
                        content="textIcon"
                        icon={presentationButtonIcon}
                        color="accent"
                        style="ghost"
                        className={presentationButtonClassName}
                        iconOrder="iconFirst"
                      />
                    </div>
                  )}
                </div>
              )}
              </div>

              {speakers && speakers.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-4 xl:gap-12">
                  {speakers.map((speaker: any, speakerIndex: number) => (
                    <div
                      key={speaker.personId || `speaker-${index}-${speakerIndex}`}
                      className="flex items-center flex-col md:flex-row gap-4 lg:gap-8 xl:gap-10"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <CircularPhoto
                          photo={speaker.photo || null}
                          alt={speaker.name || "Speaker"}
                          containerClassName="mt-4 md:mt-0 mb-2 md:mb-0"
                        />
                        <p className="font-Roboto font-[400] text-sm md:text-base lg:text-lg text-[#474F56]">
                          {SpeakerTypeEnum[speaker.speakerType as keyof typeof SpeakerTypeEnum]}
                        </p>
                      </div>
                      <div className="flex flex-col items-center md:items-start gap-0">
                        <p className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl">
                          {speaker.name}
                        </p>
                        <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                          {speaker.title ?? "Partner"}
                        </p>
                        <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                          {speaker.firmName ?? "Firm"}
                        </p>
                        {speaker.firmLogo ? (
                          <img
                            src={speaker.firmLogo}
                            alt={speaker.firmName}
                            className="h-10 max-w-[120px] w-auto mt-4 object-contain"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventHasEnded;
